import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery
} from "@material-ui/core";
import { useTheme, alpha } from "@material-ui/core/styles";
import { Close, ScheduleOutlined } from "@material-ui/icons";
import { useSnackbar } from "notistack5";
import Label from "components/Label";
import { formatPhoneLocal } from "utils/whatsapp/formatPhone";
import { TIME_OPTIONS, toDateInputValue } from "./ScheduledTemplateSendDialog";
import type { REST_schema, WhatsappCampaignStatus } from "../../../../@types/sharedSchema";
import { serverFailureMessage } from "utils/webotServerFailure";

const STATUS_LABEL: Partial<Record<WhatsappCampaignStatus, { label: string; color: "info" | "warning" | "default" }>> = {
  scheduled: { label: "מתוזמנת", color: "info" },
  running: { label: "בשליחה", color: "warning" },
  draft: { label: "טיוטה", color: "default" }
};

const formatWhen = (iso?: string): string => {
  if (!iso) return "בחלון השליחה הקרוב";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("he-IL", { dateStyle: "short", timeStyle: "short" });
};

type Props = {
  sends: REST_schema.WhatsappScheduledSendView[];
  cancelSend: (campaignId: string) => Promise<void>;
  rescheduleSend: (campaignId: string, scheduledForIso: string) => Promise<void>;
  /** Show who each send goes to — needed on the sent-log page, redundant on a contact's own tab. */
  showRecipients?: boolean;
};

/**
 * "הודעות מתוזמנות" — the contact's pending template sends, rendered at the top of the דיוור tab
 * with cancel + reschedule. Renders nothing when the list is empty (most contacts, most of the
 * time — the section must not push the history down for them).
 */
export default function ScheduledSendsSection({ sends, cancelSend, rescheduleSend, showRecipients }: Props): JSX.Element | null {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { enqueueSnackbar } = useSnackbar();
  const [rescheduleTarget, setRescheduleTarget] = useState<string | null>(null);
  const [rescheduleDay, setRescheduleDay] = useState("");
  const [rescheduleTime, setRescheduleTime] = useState("09:00");
  const [busy, setBusy] = useState(false);

  if (sends.length === 0) return null;

  const handleCancel = async (campaignId: string) => {
    try {
      await cancelSend(campaignId);
      enqueueSnackbar("השליחה בוטלה", { variant: "info" });
    } catch (err: any) {
      enqueueSnackbar(serverFailureMessage(err, "ביטול השליחה נכשל"), { variant: "error" });
    }
  };

  const openReschedule = (send: REST_schema.WhatsappScheduledSendView) => {
    // Start from the send's current slot (or tomorrow 09:00), snapped to the 15-min grid.
    const current = send.scheduledFor ? new Date(send.scheduledFor) : undefined;
    const base = current && !Number.isNaN(current.getTime()) && current.getTime() > Date.now() ? current : undefined;
    const fallback = new Date();
    fallback.setDate(fallback.getDate() + 1);
    fallback.setHours(9, 0, 0, 0);
    const at = base ?? fallback;
    setRescheduleDay(toDateInputValue(at));
    setRescheduleTime(`${String(at.getHours()).padStart(2, "0")}:${String(Math.floor(at.getMinutes() / 15) * 15).padStart(2, "0")}`);
    setRescheduleTarget(send.id);
  };

  const handleReschedule = async () => {
    if (!rescheduleTarget) return;
    const date = new Date(`${rescheduleDay}T${rescheduleTime}:00`);
    if (Number.isNaN(date.getTime()) || date.getTime() < Date.now()) {
      enqueueSnackbar("המועד חייב להיות בעתיד", { variant: "warning" });
      return;
    }
    setBusy(true);
    try {
      await rescheduleSend(rescheduleTarget, date.toISOString());
      enqueueSnackbar("המועד עודכן", { variant: "success" });
      setRescheduleTarget(null);
    } catch (err: any) {
      enqueueSnackbar(serverFailureMessage(err, "עדכון המועד נכשל"), { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{
        mt: 2,
        p: 1.5,
        borderRadius: 1.5,
        bgcolor: alpha(theme.palette.info.main, 0.06),
        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
      }}
    >
      <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
        <ScheduleOutlined sx={{ fontSize: 18, color: "info.main" }} />
        <Typography variant="subtitle2">הודעות מתוזמנות</Typography>
      </Stack>
      <Stack spacing={1}>
        {sends.map((send) => {
          const status = STATUS_LABEL[send.status];
          const movable = send.status === "scheduled" || send.status === "draft";
          return (
            <Stack key={send.id} direction="row" alignItems="center" justifyContent="space-between" gap={1}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" noWrap>
                  {send.templateName}
                  {showRecipients && send.contactIds.length > 0 && (
                    <Typography component="span" variant="body2" sx={{ color: "text.secondary" }}>
                      {" — אל "}
                      {send.contactIds.map((phone) => formatPhoneLocal(phone)).join(", ")}
                    </Typography>
                  )}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {formatWhen(send.scheduledFor)}
                </Typography>
              </Box>
              <Stack direction="row" alignItems="center" gap={0.5} sx={{ flexShrink: 0 }}>
                {status && <Label color={status.color}>{status.label}</Label>}
                {movable && (
                  <Button size="small" color="inherit" sx={{ whiteSpace: "nowrap" }} onClick={() => openReschedule(send)}>
                    שנה מועד
                  </Button>
                )}
                {send.status !== "running" && (
                  <Tooltip title="ביטול השליחה">
                    <IconButton size="small" onClick={() => void handleCancel(send.id)}>
                      <Close fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Stack>
          );
        })}
      </Stack>

      <Dialog
        open={Boolean(rescheduleTarget)}
        onClose={() => !busy && setRescheduleTarget(null)}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>שינוי מועד השליחה</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <TextField
              type="date"
              size="small"
              label="תאריך"
              sx={{ flexGrow: 1 }}
              value={rescheduleDay}
              onChange={(e) => setRescheduleDay(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel id="reschedule-time-label">שעה</InputLabel>
              <Select
                labelId="reschedule-time-label"
                label="שעה"
                value={rescheduleTime}
                onChange={(e) => setRescheduleTime(String(e.target.value))}
                MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
              >
                {TIME_OPTIONS.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" disabled={busy} onClick={() => setRescheduleTarget(null)}>
            ביטול
          </Button>
          <Button variant="contained" disabled={busy || !rescheduleDay} onClick={handleReschedule}>
            שמירה
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
